import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu'
import { Avatar } from '@trustoo/components/Avatar/v2'
import { Button } from '@trustoo/components/Button/v2'
import { Checkbox } from '@trustoo/components/Checkbox/v2'
import { clsx } from 'clsx'
import { forwardRef, type KeyboardEvent, type ReactElement, type ReactNode } from 'react'

export interface DropdownMenuItem {
    id: string
    label: ReactNode
    iconLeading?: ReactNode
    shortcut?: ReactNode
    checkbox?: boolean
    checked?: boolean
    disabled?: boolean
    destructive?: boolean
    submenu?: DropdownSection[]
    onSelect?: () => void
}

export type DropdownSection = DropdownMenuItem[]

export type DropdownHeaderType = 'avatar' | 'heading' | 'search'

export interface DropdownAvatarHeader {
    type?: 'avatar'
    name: ReactNode
    email?: ReactNode
    avatarSrc?: string
    avatarInitials?: string
}

export interface DropdownHeadingHeader {
    type: 'heading'
    title: ReactNode
    supportingText?: ReactNode
}

export interface DropdownSearchHeader {
    type: 'search'
    placeholder?: string
    value?: string
    defaultValue?: string
    onValueChange?: (value: string) => void
    'aria-label'?: string
}

export type DropdownHeader = DropdownAvatarHeader | DropdownHeadingHeader | DropdownSearchHeader

export interface DropdownFooterAction {
    label: ReactNode
    iconLeading?: ReactNode
    disabled?: boolean
    onSelect?: () => void
}

export interface DropdownFooterNote {
    start?: ReactNode
    end?: ReactNode
}

export type DropdownPlacement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'

export interface DropdownProps {
    items: DropdownSection[]
    sectionHeadings?: ReactNode[]
    header?: DropdownHeader
    footer?: DropdownSection
    footerAction?: DropdownFooterAction
    footerNote?: DropdownFooterNote
    placement?: DropdownPlacement
    open?: boolean
    defaultOpen?: boolean
    onOpenChange?: (open: boolean) => void
    children: ReactElement
    className?: string
}

const PLACEMENT_MAP: Record<DropdownPlacement, { side: 'top' | 'bottom'; align: 'start' | 'end' }> =
    {
        'bottom-start': { side: 'bottom', align: 'start' },
        'bottom-end': { side: 'bottom', align: 'end' },
        'top-start': { side: 'top', align: 'start' },
        'top-end': { side: 'top', align: 'end' },
    }

const MENU_SURFACE_CLASSES =
    'z-50 min-w-60 overflow-hidden rounded-radius-md bg-background-bg-primary font-body shadow-lg ring-1 ring-alpha-black-10'

const SUBMENU_SURFACE_CLASSES =
    'z-50 min-w-[200px] overflow-hidden rounded-radius-md bg-background-bg-primary py-spacing-xs font-body shadow-lg ring-1 ring-alpha-black-10'

const SEPARATOR_CLASSES = 'my-spacing-xs h-0 border-0 border-t border-solid border-border-secondary'

const HEADER_BORDER_CLASSES = 'border-0 border-b border-solid border-border-secondary'

const HEADER_LAYOUT_CLASSES: Record<DropdownHeaderType, string> = {
    avatar: 'flex items-center gap-spacing-md p-spacing-lg',
    heading: 'flex flex-col px-spacing-xl py-spacing-lg',
    search: 'p-spacing-lg',
}

const ITEM_ROW_CLASSES = 'group px-spacing-sm py-px outline-none data-[disabled]:cursor-not-allowed'

const ITEM_CONTENT_CLASSES =
    'flex items-center gap-spacing-xs rounded-radius-sm py-spacing-md pl-2.5 pr-spacing-sm transition-colors group-data-[highlighted]:bg-background-bg-primary_hover group-data-[state=open]:bg-background-bg-primary_hover group-data-[disabled]:opacity-50'

const ITEM_TONE_CLASSES: Record<'default' | 'destructive', { label: string; icon: string }> = {
    default: {
        label: 'text-text-secondary group-data-[highlighted]:text-text-secondary_hover',
        icon: 'text-foreground-fg-quaternary',
    },
    destructive: {
        label: 'text-text-error group-data-[highlighted]:text-text-error_hover',
        icon: 'text-foreground-fg-error-primary',
    },
}

const ChevronRightIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden className="size-4">
        <path
            d="M6 12l4-4-4-4"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const SearchIcon = () => (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden className="size-5">
        <path
            d="M17.5 17.5l-3.625-3.625m1.958-4.708a6.667 6.667 0 11-13.333 0 6.667 6.667 0 0113.333 0z"
            stroke="currentColor"
            strokeWidth={1.667}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const stopMenuKeyboardHandling = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
        return
    }
    event.stopPropagation()
}

interface MenuItemContentProps {
    item: DropdownMenuItem
    hasSubmenu: boolean
}

const MenuItemContent = ({ item, hasSubmenu }: MenuItemContentProps) => {
    const tone = ITEM_TONE_CLASSES[item.destructive ? 'destructive' : 'default']

    return (
        <div className={ITEM_CONTENT_CLASSES}>
            <div className="flex min-w-0 flex-1 items-center gap-spacing-md">
                {item.checkbox && (
                    <Checkbox
                        size="sm"
                        checked={Boolean(item.checked)}
                        disabled={item.disabled}
                        className="pointer-events-none shrink-0"
                    />
                )}
                {item.iconLeading && (
                    <span
                        className={clsx(
                            'flex size-4 shrink-0 items-center justify-center',
                            tone.icon,
                        )}>
                        {item.iconLeading}
                    </span>
                )}
                <span className={clsx('min-w-0 flex-1 truncate text-sm font-semibold', tone.label)}>
                    {item.label}
                </span>
            </div>
            {item.shortcut && (
                <span className="shrink-0 pr-spacing-xs text-xs font-medium text-text-quaternary">
                    {item.shortcut}
                </span>
            )}
            {hasSubmenu && (
                <span className="shrink-0 text-foreground-fg-quaternary">
                    <ChevronRightIcon />
                </span>
            )}
        </div>
    )
}

interface SectionListProps {
    sections: DropdownSection[]
    headings?: ReactNode[]
}

const MenuItemRow = ({ item }: { item: DropdownMenuItem }) => {
    if (item.submenu && item.submenu.length > 0) {
        return (
            <RadixDropdownMenu.Sub>
                <RadixDropdownMenu.SubTrigger disabled={item.disabled} className={ITEM_ROW_CLASSES}>
                    <MenuItemContent item={item} hasSubmenu />
                </RadixDropdownMenu.SubTrigger>
                <RadixDropdownMenu.Portal>
                    <RadixDropdownMenu.SubContent
                        sideOffset={4}
                        collisionPadding={16}
                        className={SUBMENU_SURFACE_CLASSES}>
                        <SectionList sections={item.submenu} />
                    </RadixDropdownMenu.SubContent>
                </RadixDropdownMenu.Portal>
            </RadixDropdownMenu.Sub>
        )
    }

    if (item.checkbox) {
        return (
            <RadixDropdownMenu.CheckboxItem
                disabled={item.disabled}
                checked={Boolean(item.checked)}
                onSelect={(event) => {
                    event.preventDefault()
                    item.onSelect?.()
                }}
                className={ITEM_ROW_CLASSES}>
                <MenuItemContent item={item} hasSubmenu={false} />
            </RadixDropdownMenu.CheckboxItem>
        )
    }

    return (
        <RadixDropdownMenu.Item
            disabled={item.disabled}
            onSelect={item.onSelect}
            className={ITEM_ROW_CLASSES}>
            <MenuItemContent item={item} hasSubmenu={false} />
        </RadixDropdownMenu.Item>
    )
}

const SectionList = ({ sections, headings }: SectionListProps) => {
    const visible = sections
        .map((section, index) => ({ section, heading: headings?.[index], index }))
        .filter(({ section }) => section.length > 0)

    return (
        <>
            {visible.map(({ section, heading, index }, position) => (
                <RadixDropdownMenu.Group key={`section-${index}`}>
                    {position > 0 && <RadixDropdownMenu.Separator className={SEPARATOR_CLASSES} />}
                    {heading && (
                        <RadixDropdownMenu.Label className="px-spacing-xl pb-spacing-xxs pt-spacing-sm text-xs font-semibold text-text-brand-secondary">
                            {heading}
                        </RadixDropdownMenu.Label>
                    )}
                    {section.map((item) => (
                        <MenuItemRow key={item.id} item={item} />
                    ))}
                </RadixDropdownMenu.Group>
            ))}
        </>
    )
}

const MenuHeader = ({ header }: { header: DropdownHeader }) => {
    if (header.type === 'heading') {
        return (
            <div className={clsx(HEADER_LAYOUT_CLASSES.heading, HEADER_BORDER_CLASSES)}>
                <span className="text-sm font-semibold text-text-primary">{header.title}</span>
                {header.supportingText && (
                    <span className="text-sm text-text-tertiary">{header.supportingText}</span>
                )}
            </div>
        )
    }

    if (header.type === 'search') {
        return (
            <div className={clsx(HEADER_LAYOUT_CLASSES.search, HEADER_BORDER_CLASSES)}>
                <label className="flex items-center gap-spacing-md rounded-radius-md border border-solid border-border-primary bg-background-bg-primary px-spacing-lg py-spacing-md shadow-xs focus-within:border-border-brand focus-within:ring-1 focus-within:ring-inset focus-within:ring-focus-ring">
                    <span className="shrink-0 text-foreground-fg-quaternary">
                        <SearchIcon />
                    </span>
                    <input
                        type="search"
                        placeholder={header.placeholder ?? 'Search'}
                        aria-label={header['aria-label'] ?? header.placeholder ?? 'Search'}
                        value={header.value}
                        defaultValue={header.defaultValue}
                        onChange={(event) => header.onValueChange?.(event.target.value)}
                        onKeyDown={stopMenuKeyboardHandling}
                        className="min-w-0 flex-1 border-0 bg-base-transparent p-spacing-none text-md text-text-primary outline-none placeholder:text-text-placeholder"
                    />
                </label>
            </div>
        )
    }

    const hasAvatar = Boolean(header.avatarSrc) || Boolean(header.avatarInitials)

    return (
        <div className={clsx(HEADER_LAYOUT_CLASSES.avatar, HEADER_BORDER_CLASSES)}>
            {hasAvatar && (
                <Avatar size="md" src={header.avatarSrc} initials={header.avatarInitials} ring />
            )}
            <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-semibold text-text-primary">
                    {header.name}
                </span>
                {header.email && (
                    <span className="truncate text-sm text-text-tertiary">{header.email}</span>
                )}
            </div>
        </div>
    )
}

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
    (
        {
            items,
            sectionHeadings,
            header,
            footer,
            footerAction,
            footerNote,
            placement = 'bottom-start',
            open,
            defaultOpen,
            onOpenChange,
            children,
            className,
        },
        ref,
    ) => {
        const { side, align } = PLACEMENT_MAP[placement]
        const footerItems = footer ?? []

        return (
            <RadixDropdownMenu.Root
                open={open}
                defaultOpen={defaultOpen}
                onOpenChange={onOpenChange}>
                <RadixDropdownMenu.Trigger asChild>{children}</RadixDropdownMenu.Trigger>

                <RadixDropdownMenu.Portal>
                    <RadixDropdownMenu.Content
                        ref={ref}
                        side={side}
                        align={align}
                        sideOffset={6}
                        collisionPadding={16}
                        className={clsx(MENU_SURFACE_CLASSES, className)}>
                        {header && <MenuHeader header={header} />}

                        <div className="py-spacing-xs">
                            <SectionList sections={items} headings={sectionHeadings} />
                            {footerItems.length > 0 && (
                                <>
                                    <RadixDropdownMenu.Separator className={SEPARATOR_CLASSES} />
                                    <SectionList sections={[footerItems]} />
                                </>
                            )}
                        </div>

                        {footerAction && (
                            <div className="border-0 border-t border-solid border-border-secondary p-spacing-lg">
                                <RadixDropdownMenu.Item
                                    asChild
                                    disabled={footerAction.disabled}
                                    onSelect={footerAction.onSelect}>
                                    <Button
                                        type="button"
                                        size="xs"
                                        hierarchy="secondary"
                                        iconLeading={footerAction.iconLeading}
                                        disabled={footerAction.disabled}
                                        className="w-full justify-center outline-none data-[highlighted]:bg-background-bg-primary_hover">
                                        {footerAction.label}
                                    </Button>
                                </RadixDropdownMenu.Item>
                            </div>
                        )}

                        {footerNote && (
                            <div className="flex items-center justify-between gap-spacing-lg border-0 border-t border-solid border-border-secondary px-spacing-xl py-spacing-lg text-sm text-text-tertiary">
                                <span>{footerNote.start}</span>
                                <span>{footerNote.end}</span>
                            </div>
                        )}
                    </RadixDropdownMenu.Content>
                </RadixDropdownMenu.Portal>
            </RadixDropdownMenu.Root>
        )
    },
)

Dropdown.displayName = 'Dropdown'
